"use client";

import React from "react";
import { motion } from "framer-motion";
import { Check, ChevronRight, Shield, Star, Zap, Globe, Server, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const vpns = [{
  id: "nord",
  name: "NordVPN",
  rating: "9.8",
  features: ["5400+ Servers", "Double VPN", "Strict No-Logs"],
  prices: {
    mo1: "$12.99",
    yr1: "$4.99",
    yr2: "$3.09"
  },
  link: "/NordVPNReviewPage",
  affiliateUrl: "https://vpngenix.com/go/NordVPN",
  isSpecial: false,
  badge: "Editor's Choice"
}, {
  id: "surf",
  name: "SurfShark",
  rating: "9.6",
  features: ["Unlimited Devices", "CleanWeb", "Bypasser"],
  prices: {
    mo1: "$10.99",
    yr1: "$3.99",
    yr2: "$2.49"
  },
  link: "/SurfsharkReviewPage",
  affiliateUrl: "https://vpngenix.com/go/SurfShark",
  isSpecial: false,
  badge: "Best Value"
}, {
  id: "expr",
  name: "ExpressVPN",
  rating: "9.5",
  features: ["Lightway Protocol", "TrustedServer", "Threat Manager"],
  prices: {
    mo1: "$12.95",
    yr1: "$6.67",
    yr2: "N/A"
  },
  link: "/ExpressVPNReviewPage",
  affiliateUrl: "https://vpngenix.com/go/ExpressVPN",
  isSpecial: false,
  badge: "Fastest Speeds"
}, {
  id: "cybe",
  name: "CyberGhost",
  rating: "9.2",
  features: ["Streaming Servers", "NoSpy Servers", "WireGuard"],
  prices: {
    mo1: "$12.99",
    yr1: "$4.29",
    yr2: "$2.19"
  },
  link: "/VpnReviewsPage",
  affiliateUrl: "https://vpngenix.com/go/CyberGhost",
  isSpecial: false
}, {
  id: "malw",
  name: "Malwarebytes Privacy",
  rating: "9.0",
  features: ["Next-Gen VPN", "No-Log Policy", "256-bit Encryption"],
  prices: {
    mo1: "$9.99",
    yr1: "$3.33",
    yr2: "N/A"
  },
  link: "/MalwarebytesPage",
  affiliateUrl: "https://www.malwarebytes.com/privacy?a_aid=9999",
  isSpecial: true,
  badge: "Specialized Security"
}, {
  id: "prot",
  name: "ProtonVPN",
  rating: "8.9",
  features: ["Secure Core", "Swiss Privacy", "NetShield"],
  prices: {
    mo1: "$9.99",
    yr1: "$4.99",
    yr2: "$4.49"
  },
  link: "/VpnReviewsPage",
  affiliateUrl: "https://vpngenix.com/go/ProtonVPN",
  isSpecial: false
}, {
  id: "pia",
  name: "Private Internet Access",
  rating: "8.8",
  features: ["Next-Gen Servers", "MACE Ad Blocker", "Open Source"],
  prices: {
    mo1: "$11.95",
    yr1: "$3.33",
    yr2: "$2.19"
  },
  link: "/VpnReviewsPage",
  affiliateUrl: "https://vpngenix.com/go/PIA",
  isSpecial: false,
  badge: "Best Value"
}, {
  id: "fsec",
  name: "F-Secure",
  rating: "8.5",
  features: ["Real-Time Malware Protection", "Banking & Browsing Protection", "Family & Parental Controls", "Built-in VPN with Secure Browsing"],
  prices: {
    mo1: "$5.99",
    yr1: "$3.33",
    yr2: "N/A"
  },
  link: "/VpnReviewsPage",
  affiliateUrl: "https://vpngenix.com/go/F-Secure",
  isSpecial: true,
  badge: "All-in-One VPN & Security"
}, {
  id: "mcaf",
  name: "McAfee",
  rating: "8.3",
  features: ["Real-Time Antivirus Protection", "Built-in VPN", "Identity Theft Monitoring", "Password Manager Included"],
  prices: {
    mo1: "$5.99",
    yr1: "$2.95",
    yr2: "N/A"
  },
  link: "/VpnReviewsPage",
  affiliateUrl: "https://vpngenix.com/go/McAfee",
  isSpecial: true,
  badge: "All-in-One Antivirus & Identity Protection"
}];
const tableData = [{
  name: "NordVPN",
  servers: "5,400+",
  countries: "60",
  speed: "Very Fast",
  category: "Overall Best"
}, {
  name: "Surfshark",
  servers: "3,200+",
  countries: "100",
  speed: "Fast",
  category: "Best Value"
}, {
  name: "ExpressVPN",
  servers: "3,000+",
  countries: "94",
  speed: "Very Fast",
  category: "Best for Streaming"
}, {
  name: "CyberGhost",
  servers: "9,000+",
  countries: "91",
  speed: "Average",
  category: "Best for Beginners"
}, {
  name: "Malwarebytes",
  servers: "500+",
  countries: "40",
  speed: "Fast",
  category: "Best Security Bundle"
}, {
  name: "ProtonVPN",
  servers: "2,900+",
  countries: "67",
  speed: "Fast",
  category: "Best for Privacy"
}, {
  name: "Private Internet Access",
  servers: "10,000+",
  countries: "84",
  speed: "Very Fast",
  category: "Best Customization"
}, {
  name: "McAfee",
  servers: "N/A",
  countries: "N/A",
  speed: "Fast",
  category: "Best Antivirus & Identity Protection"
}];
const faqs = [{
  q: "What is the best VPN overall?",
  a: "Based on our extensive testing, NordVPN consistently ranks as the top choice due to its balance of speed, security features, and massive server network."
}, {
  q: "Do I really need a VPN?",
  a: "Yes, if you value online privacy, frequently use public Wi-Fi, or want to access geo-restricted content, a VPN is an essential tool for digital security."
}, {
  q: "Are free VPNs safe?",
  a: "Most free VPNs are not recommended as they often log your data, have slow speeds, and may even inject malware. It's better to use a reputable paid service."
}, {
  q: "Will a VPN slow down my internet?",
  a: "A slight drop in speed is normal due to encryption overhead, but top-tier VPNs minimize this to the point where it's barely noticeable during normal browsing or streaming."
}, {
  q: "Can I use a VPN on my phone?",
  a: "Absolutely. All the top providers offer dedicated, easy-to-use applications for both iOS and Android devices."
}, {
  q: "Is it legal to use a VPN?",
  a: "In most countries, using a VPN is perfectly legal. However, illegal activities remain illegal whether you use a VPN to mask your identity or not."
}];
const vpnArticleKeywords: Record<string, string[]> = {
  NordVPN: ["nord", "nordvpn", "meshnet", "double vpn", "nordlynx"],
  Surfshark: ["surfshark", "cleanweb", "unlimited", "bypasser"],
  ExpressVPN: ["expressvpn", "lightway", "trustedserver", "router"],
  CyberGhost: ["cyberghost", "nospy", "streaming server"],
  "Malwarebytes Privacy": ["malwarebytes", "privacy", "security bundle"],
  ProtonVPN: ["proton", "protonvpn", "secure core", "netshield"],
  "Private Internet Access": ["private internet access", "pia", "mace"],
  "F-Secure": ["f-secure", "fsecure", "vpn", "malware protection", "banking protection"],
  McAfee: ["mcafee", "antivirus", "identity protection", "malware"]
};
function getRelatedArticles(posts: any[], vpnName: string) {
  const keywords = vpnArticleKeywords[vpnName] || [vpnName.toLowerCase()];
  const scored = posts.map(post => {
    const title = (post.title || "").toLowerCase();
    const slug = (post.slug || "").toLowerCase();
    let score = 0;
    keywords.forEach(kw => {
      if (title.includes(kw)) score += 2;
      if (slug.includes(kw)) score += 1;
    });
    return {
      post,
      score
    };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map(s => s.post);
}
function VpnCardsGrid() {
  const {
    posts
  } = usePosts();
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-24">
      {vpns.map((vpn, index) => {
      const related = getRelatedArticles(posts, vpn.name);
      return <motion.div key={vpn.id} data-index={index} variants={itemVariants} className="relative group">
            <Card className={`h-full flex flex-col relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${vpn.isSpecial ? 'border-primary shadow-lg shadow-primary/10' : 'border-border/50'}`}>
              
              {}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50 rounded-tl-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary/50 rounded-tr-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary/50 rounded-bl-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary/50 rounded-br-xl opacity-0 group-hover:opacity-100 transition-opacity" />

              {vpn.isSpecial && <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                  Specialized
                </div>}

              <CardHeader className="pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    {vpn.badge && <Badge variant="secondary" className="mb-2 bg-secondary/20 text-secondary-foreground hover:bg-secondary/30">
                        {vpn.badge}
                      </Badge>}
                    <CardTitle className="text-2xl font-default">{vpn.name}</CardTitle>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-1 text-primary">
                      <Star className="size-5 fill-current" />
                      <span className="text-2xl font-bold font-mono">{vpn.rating}</span>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">/ 10</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-grow">
                <ul className="space-y-3 mb-6">
                  {vpn.features.map((feature, i) => <li key={i} data-index={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="rounded-lg bg-primary/10 p-1 shrink-0">
                        <Check className="size-3 text-primary" />
                      </div>
                      <span className="font-sans">{feature}</span>
                    </li>)}
                </ul>

                <Separator className="my-4 opacity-50" />

                <h3 className="text-sm text-muted-foreground font-medium mb-2">Related Articles</h3>
                <ul className="list-none space-y-2 mb-6">
                  {related.length > 0 ? related.map((art: any, artIdx: number) => <li key={art.id || artIdx} data-index={artIdx}>
                        <Link to={art.slug ? `/Blogs?slug=${art.slug}` : `/Blogs?post=${art.id}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center justify-between group/art">
                          <span className="truncate pr-1">{art.title}</span>
                          <ChevronRight className="ml-1 size-3 transition-transform group-hover/art:translate-x-0.5 shrink-0" />
                        </Link>
                      </li>) : <li className="text-xs text-muted-foreground/60 italic">No related articles found</li>}
                </ul>

                <Separator className="my-4 opacity-50" />

                {}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded-md bg-muted/30">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">1 Mo</div>
                    <div className="font-semibold text-sm">{vpn.prices.mo1}</div>
                  </div>
                  <div className="p-2 rounded-md bg-muted/30">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">1 Yr</div>
                    <div className="font-semibold text-sm">{vpn.prices.yr1}</div>
                  </div>
                  <div className="p-2 rounded-md bg-primary/10 border border-primary/20 relative">
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[8px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                      BEST DEAL
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-primary font-mono mb-1 mt-1">2 Yr</div>
                    <div className="font-bold text-primary text-sm">{vpn.prices.yr2}</div>
                  </div>
                </div>
              </CardContent>

              <CardFooter>
                <Button asChild className="w-full group/btn" size="lg">
                  <Link to={vpn.affiliateUrl} newTab>
                    Get {vpn.name}
                    <ChevronRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>;
    })}
    </div>;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function VpnRankingsHero() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: isCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: faqRef,
    isVisible: isFaqVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const wp_query_params = {
    post_type: "post",
    per_page: 50
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="vpnrankingshero" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-50" />

      <div className="container mx-auto px-6 relative z-10">
        {}
        <motion.div ref={headerRef} initial="hidden" animate={isHeaderVisible ? "show" : "hidden"} variants={containerVariants} className="max-w-3xl mx-auto text-center mb-20">
          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-bold font-default tracking-tight mb-6">
            The Ultimate <span className="text-primary">VPN Rankings</span>
          </motion.h1>
          <motion.p variants={itemVariants} className="text-lg md:text-xl text-muted-foreground font-sans">
            We tested over 50 providers to bring you the definitive list of the most secure, fastest, and reliable VPNs on the market.
          </motion.p>
        </motion.div>

        {}
        <motion.div ref={cardsRef} initial="hidden" animate={isCardsVisible ? "show" : "hidden"} variants={containerVariants}>
          <PostsProvider wp_query={{
          post_type: "post",
          per_page: 50,
          status: ["publish"]
        }}>
            <VpnCardsGrid />
          </PostsProvider>
        </motion.div>

        {}
        <motion.div ref={tableRef} initial="hidden" animate={isTableVisible ? "show" : "hidden"} variants={containerVariants} className="mb-24">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold font-default mb-4">Detailed Comparison</h2>
            <p className="text-muted-foreground font-sans max-w-2xl mx-auto">
              Dive deeper into the technical specifications of our top-rated providers to find the perfect match for your specific needs.
            </p>
          </div>

          <Card className="border-border/50 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="hover:bg-transparent border-border/50">
                    <TableHead className="w-[200px] font-mono text-xs uppercase tracking-wider">Provider</TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Server className="size-4" /> Servers
                      </div>
                    </TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Globe className="size-4" /> Countries
                      </div>
                    </TableHead>
                    <TableHead className="font-mono text-xs uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Zap className="size-4" /> Speed
                      </div>
                    </TableHead>
                    <TableHead className="text-right font-mono text-xs uppercase tracking-wider">Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableData.map((row, index) => <TableRow key={index} data-index={index} className="border-border/50 hover:bg-muted/20 transition-colors">
                      <TableCell className="font-medium font-default">{row.name}</TableCell>
                      <TableCell className="font-sans text-muted-foreground">{row.servers}</TableCell>
                      <TableCell className="font-sans text-muted-foreground">{row.countries}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-background/50 font-sans">
                          {row.speed}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-sans text-muted-foreground">{row.category}</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </Card>
        </motion.div>

        {}
        <motion.div ref={faqRef} initial="hidden" animate={isFaqVisible ? "show" : "hidden"} variants={containerVariants} className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold font-default mb-4">Frequently Asked Questions</h2>
            <p className="text-muted-foreground font-sans">
              Everything you need to know about choosing and using a VPN.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => <AccordionItem key={index} data-index={index} value={`item-${index}`} className="border border-border/50 rounded-lg px-6 bg-card/50 data-[state=open]:bg-card transition-colors">
                <AccordionTrigger className="text-left font-default hover:no-underline py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-sans leading-relaxed pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>
      </div>
    </section>;
}